import { Component, DestroyRef, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { DOCUMENT } from '@angular/common';
import { DateAdapter } from '@angular/material/core';
import { RouterOutlet } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';
import { LocaleService } from '@next/core-lib/i18n';
import { WrapperSiteSidenavComponent } from '@next/core-lib/wrapper-site-sidenav';
import { startWith } from 'rxjs';

const supportedDatepickerLocales = new Set(['de', 'en', 'fr', 'nl']);

const normalizeDatepickerLocale = (locale: string): string => {
  const language = locale.toLowerCase().split('-')[0];
  return supportedDatepickerLocales.has(language) ? language : 'en';
};

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  imports: [RouterOutlet, WrapperSiteSidenavComponent],
})
export class AppComponent {
  private readonly locale = inject(LocaleService);
  private readonly dateAdapter = inject<DateAdapter<unknown>>(DateAdapter);
  private readonly destroyRef = inject(DestroyRef);
  private readonly serviceWorkerUpdate = inject(SwUpdate);
  private readonly document = inject(DOCUMENT);


  direction$ = this.locale.direction$;
  constructor() {
    this.syncDatepickerLocale();
    this.syncHtmlLang();
    this.checkWithServiceWorkerForNewVersion();
  }

  private syncDatepickerLocale(): void {
    this.locale.localeDataChanged$
      .pipe(startWith(undefined), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.dateAdapter.setLocale(normalizeDatepickerLocale(this.locale.getLocale()));
      });
  }

  private syncHtmlLang(): void {
    this.locale.locale$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((locale) => {
        this.document.documentElement.lang = locale;
      });
  }

  checkWithServiceWorkerForNewVersion = async () => {
    try {
      // Check if Service Worker is supported by the Browser
      if (this.serviceWorkerUpdate.isEnabled) {
        // Check if new version is available
        const isNewVersion = await this.serviceWorkerUpdate.checkForUpdate();
        if (isNewVersion) {
          // Check if new version is activated
          const isNewVersionActivated = await this.serviceWorkerUpdate.activateUpdate();

          // Reload the application with new version if new version is activated
          if (isNewVersionActivated) {
            window.location.reload();
          }
        }
      }
    } catch (error) {
      console.log(
        'Service Worker - Error when checking for new version of the application: ',
        error,
      );
      window.location.reload();
    }
  };
}
